import styled from "styled-components";
import { Color, Device, Font, Spacing } from "@styles/constants";
import { rem } from "@styles/helpers";
import { SmallBodyMedium, SmallBodyMediumCSS } from "@styles/typography";

export const AccountWrapper = styled.div`
  padding-top: ${Spacing.contentPageLarge};

  @media print {
    padding-top: 0;
  }
`;

export const Sidebar = styled.aside`
  ul {
    margin-top: ${rem(80)};
    margin-bottom: ${rem(32)};
    list-style: none;
  }

  li {
    text-align: right;
    text-transform: uppercase;
    margin-bottom: ${rem(16)};
  }

  a {
    display: inline-block;
    font-family: ${Font.semibold};
    text-decoration: none;
    color: ${Color.grayMedium2};
    letter-spacing: 1px;

    &.active {
      color: ${Color.blackBrand};
    }
  }

  @media ${Device.small} {
    ul {
      margin-top: 0;
    }

    li {
      text-align: left;
    }
  }

  @media print {
    display: none;
  }
`;

export const AccountContent = styled.div`
  address {
    font-style: normal;
  }

  @media print {
    width: 100%;
  }
`;

export const AccountSection = styled.div`
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.1);
  padding: ${rem(32)} ${rem(48)} ${rem(48)} ${rem(48)};
  margin-bottom: ${rem(48)};

  @media ${Device.mediumDown} {
    padding: ${rem(30)};
  }

  @media ${Device.small} {
    box-shadow: none;
    padding: ${rem(8)};
  }

  button {
    text-decoration: underline;
    cursor: pointer;
  }

  input[type="submit"] {
    margin-bottom: 0;

    @media ${Device.small} {
      width: 100%;
    }
  }

  dl {
    width: 100%;
    overflow: hidden;
    padding: 0;
    margin: 0;
  }

  dt,
  dd {
    float: left;
    width: 50%;
    padding: 0;
    margin: 0;

    &:not(:last-of-type) {
      margin-bottom: ${rem(16)};
    }
  }

  @media ${Device.mediumDown} {
    dt {
      width: 100%;
      margin-bottom: 0;
    }
  }

  header:nth-of-type(2) {
    margin-top: ${rem(40)};
  }
`;

export const AccountHeader = styled.header`
  padding-bottom: ${rem(16)};
  border-bottom: 1px solid ${Color.grayMedium};
  margin-bottom: ${rem(16)};
`;

export const NonActiveSubscription = styled.div`
  color: ${Color.grayMedium2};
  padding-top: ${rem(24)};
  text-align: center;

  p:last-of-type {
    margin-bottom: 0;
  }

  button {
    text-decoration: none;
  }
`;

export const ButtonWrap = styled.div`
  margin-top: ${rem(16)};
`;

export const InlineUpdateButton = styled.button`
  font-family: ${Font.semibold};
`;

export const CancelButton = styled.button`
  margin-left: ${rem(8)};
  margin-top: 0;
  display: inline-block;

  @media ${Device.small} {
    font-size: ${rem(16)};
    padding: ${rem(10)} ${rem(10)} ${rem(10)} 0;
    margin: ${rem(5)} 0 0;
  }
`;

export const NoResults = styled.p`
  padding: ${rem(24)} 0 ${rem(40)};
  margin-bottom: 0;
  text-align: center;
  border-bottom: 1px solid ${Color.grayMedium};
`;

export const SubscriptionNote = styled.p`
  color: ${Color.grayMedium2};
  font-size: 90%;
`;

export const GeneralDisclaimer = styled(SmallBodyMedium)`
  margin-bottom: ${rem(16)};
`;

export const StyledDisclaimer = styled.div`
  padding-bottom: ${rem(16)};
  margin-bottom: ${rem(16)};
  border-bottom: 1px solid ${Color.grayMedium};

  & > div {
    ${SmallBodyMediumCSS};
  }
`;

export const DigitalDetails = styled.dl`
  @media ${Device.largeUp} {
    dt.disclaimer,
    dd.disclaimer {
      width: 100%;
    }
  }

  @media ${Device.mediumDown} {
    dt,
    dd {
      width: 100%;
    }
  }

  dt.disclaimer {
    margin-bottom: ${rem(5)};
  }

  dd,
  dd > div {
    font-size: ${rem(16)};
  }

  p:last-of-type {
    margin-bottom: 0;
  }
`;

export const OrderList = styled.article`
  dl {
    border-bottom: 1px solid ${Color.grayMedium};

    &:not(:last-of-type) {
      margin-bottom: ${rem(24)};
    }
  }

  dt {
    width: 30%;

    @media ${Device.mediumDown} {
      width: 100%;
    }
  }

  dd {
    width: 70%;

    @media ${Device.mediumDown} {
      width: 100%;
    }
  }

  ul,
  li {
    list-style: none;
    margin: 0;
    padding: 0;
  }
`;

export const TrackingRow = styled.li`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;

  && {
    margin-bottom: ${rem(12)};

    @media ${Device.small} {
      margin-bottom: ${rem(20)};
    }
  }

  p {
    margin-bottom: 0;
    margin-right: ${rem(8)};
  }

  div:first-of-type {
    flex: 0 1 65%;
  }
`;

export const TrackingItems = styled.div`
  flex: 0 1 35%;
  text-align: right;

  a {
    font-family: ${Font.semibold};
    font-size: ${rem(16)};
    white-space: nowrap;
  }
`;

export const PaymentInfo = styled.div`
  h5 {
    margin: ${rem(16)} 0;
    text-align: left;
    text-transform: inherit;
    letter-spacing: inherit;
    width: 100%;
  }

  section:not(:last-of-type) {
    margin-bottom: ${rem(64)};
  }

  a {
    margin-top: ${rem(16)};
  }
`;

export const PaymentTable = styled.table`
  thead {
    background: transparent;
    border: none;
    border-bottom: 1px solid ${Color.grayMedium};
  }

  tbody {
    border: none;

    tr {
      border-bottom: 1px solid ${Color.grayMedium};
    }

    tr:nth-child(even) {
      background: transparent;
    }
  }
`;

// Order Summary
export const OrderHeader = styled.div`
  border-bottom: 1px solid ${Color.grayMedium};
  margin-bottom: ${rem(32)};
`;

export const OrderNumberWrap = styled.div`
  margin-bottom: ${rem(16)};
  align-items: center;
`;

export const OrderNumber = styled.h4`
  font-family: ${Font.semibold};
`;

export const PrintButton = styled.button`
  display: flex;
  align-items: center;
  text-decoration: none;
  text-transform: uppercase;
  margin-left: auto;

  svg {
    display: inline-block;
    width: 18px;
    margin-right: ${rem(10)};
  }
`;

export const RightAlignedContent = styled.div`
  text-align: right;

  @media ${Device.mediumDown} {
    text-align: left;
    margin-top: ${rem(16)};
  }
`;

export const OrderDetails = styled.dl`
  @media ${Device.mediumDown} {
    dt,
    dd {
      width: 50%;
    }

    dd {
      text-align: right;
    }
  }
`;

export const PaymentMethod = styled.dd`
  display: flex;

  span.credit-card {
    display: flex;
    width: 40px;
    height: 25px;
    margin-right: ${rem(5)};
  }
`;

export const Alert = styled.div`
  padding: ${rem(12)} ${rem(16)};
  margin: ${rem(32)}; 0;
  font-size: ${rem(14)};
  font-family: ${Font.semibold};
  background: ${Color.blackBrand};
  color: ${Color.white};
`;

// Account Transfer
export const SubText = styled.p`
  font-size: ${rem(14)};
  color: ${Color.grayMedium3};
  line-height: 1;
`;

export const OutgoingTransfer = styled.div`
  padding-bottom: ${rem(8)};
  margin-top: ${rem(8)};
  border-bottom: 1px solid black;

  p {
    margin-bottom: 0;
  }
`;
